import {
	DEFAULT_PANTONE_BOOKS,
	PANTONE_ACCURACY,
	pantoneBookTitle,
	type PaletteColorPantoneInput,
} from '@codezo/brand-detail';
import {
	PantoneBook,
	useGetAllPantoneColorQuery,
	useMatchPantoneColorQuery,
} from '@codezo/graphql';
import { ChevronDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fieldFocus, fieldSize, fieldSurface } from '../../components/ui/field-surface';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { SelectSearchInput } from '../../components/ui/select-primitives';
import { cn } from '../../lib/utils';

/**
 * The print spec on a brand colour: which PANTONE® book, and which code in it.
 *
 * ── Why it is a field and not an action ──
 *
 * This started as an "Add Pantone reference" button that unfolded a panel, and
 * it was the only thing in the form phrased as something you DO. Name, hex,
 * category and usage are all just boxes — you fill them or you leave them. A
 * colour's print spec is the same kind of thing, optional in exactly the same
 * way, so it wears the same field recipe as the controls beside it and opens a
 * popover the way the category select does.
 *
 * ── Two ways to find a swatch ──
 *
 * Sometimes the screen colour exists first — picked, or lifted off a logo — and
 * the question is "what do we print this as". Then `matchPantoneColor` ranks the
 * catalogue against that hex, closest first, which is the fan deck's job done in
 * one step.
 *
 * But a colour can just as easily start life AS a print spec: the client
 * specifies 186 C and the screen value follows. So the ranking and the plain
 * catalogue sit side by side as tabs, both always reachable, and picking a
 * swatch hands its hex back up — which is what lets someone define a brand
 * colour from a code alone. Only which tab OPENS is inferred, and only as a
 * default; neither locks the other out.
 *
 * ── Why there is no book filter ──
 *
 * There was a row of eight chips. It repeated "PANTONE®" eight times inside a
 * panel that is already nothing but Pantone, overflowed so half the books were
 * off-screen, and asked the user which book — a question the result itself
 * answers. The book is a heading over its own group now, and the only control
 * left is the one real decision: whether to look past the solid books at all.
 *
 * Solid Coated and Solid Uncoated are the default because the same ink prints
 * differently on each and that is the choice a print buyer actually makes. All
 * eight adds the Color Bridge and Extended Gamut renderings of the same ink —
 * near-identical rows that help nobody until someone wants metallics or neons.
 *
 * ── What is deliberately not promised ──
 *
 * `exact` means the book's SCREEN value matches, not that the ink will
 * reproduce the colour. And a `distant` best result is real information — no
 * spot ink reaches this colour, the normal answer for neons and bright greens —
 * so it is said plainly instead of presenting the nearest row as "your match".
 */

const SOLID_BOOKS = DEFAULT_PANTONE_BOOKS as unknown as PantoneBook[];

/** A row in either list. `accuracy` is only ever set by a match — browsing and
 * searching return the catalogue, which carries no distance. */
interface CatalogueRow {
	color: {
		id: string;
		book: PantoneBook;
		bookTitle: string;
		code: string;
		name?: string | null;
		hex: string;
	};
	accuracy: string | null;
}

interface PickedSwatch {
	book: PantoneBook;
	code: string;
	hex: string;
}

/** The book's name without the brand it is already sitting under. */
const bookLabel = (title: string) => title.replace(/^PANTONE®?\s*/i, '');

export function PantoneField({
	/**
	 * The screen colour to rank against, or null when the user hasn't chosen
	 * one. Null drops the ranking tab entirely — there is nothing to rank by —
	 * which is the case this field exists to support: a hex is not a
	 * precondition for specifying print.
	 */
	hex,
	value,
	onChange,
}: {
	hex: string | null;
	value: PaletteColorPantoneInput | null;
	/** `swatch` carries the book's own hex so the caller can adopt it as the
	 * screen colour when there isn't one yet. */
	onChange: (next: PaletteColorPantoneInput | null, swatch?: PickedSwatch) => void;
}) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [allBooks, setAllBooks] = useState(false);
	/* A choice, not something inferred from whether a hex exists — an existing
	   colour always has one, so deriving it left browse unreachable for every
	   colour that already existed. */
	const [browsing, setBrowsing] = useState(false);

	const query = search.trim();
	const books = allBooks ? undefined : SOLID_BOOKS;
	const canMatch = Boolean(hex);
	const mode = query ? 'search' : canMatch && !browsing ? 'match' : 'browse';

	const { data: matchData, loading: matching } = useMatchPantoneColorQuery({
		variables: { input: { hex: hex ?? '#000000', books, limit: 12 } },
		fetchPolicy: 'no-cache',
		skip: !open || mode !== 'match',
	});

	/**
	 * Browsing is paged, and the pages accumulate.
	 *
	 * Fan-deck order means page one of Solid Coated is yellows — the book opens
	 * on Yellow C and works through orange, red, pink, purple, blue, green, then
	 * the neutrals. A single page of that reads as "this picker only has
	 * yellow", so the rest has to be reachable.
	 *
	 * `page` resets whenever the thing being listed changes, which is what
	 * `listKey` tracks — otherwise page 3 of a search would append onto page 3
	 * of the previous one.
	 */
	const listKey = `${mode}:${query}:${allBooks}`;
	const [page, setPage] = useState(1);
	const [pagedRows, setPagedRows] = useState<CatalogueRow[]>([]);

	useEffect(() => {
		setPage(1);
		setPagedRows([]);
	}, [listKey]);

	const { data: listData, loading: listing } = useGetAllPantoneColorQuery({
		variables: {
			input: {
				search: query || undefined,
				books,
				/* No `order` — the default is `positionInBook`, the order the
				   physical book is in. Sorting by code scatters it: codes are
				   not numbers and not uniformly shaped ("186 C", "Bright Red C"). */
				pagination: { currentPage: page, limit: 60 },
			},
		},
		fetchPolicy: 'no-cache',
		skip: !open || mode === 'match',
		onCompleted: (result) => {
			const incoming = result.getAllPantoneColor.data.map((color) => ({
				color,
				accuracy: null,
			}));
			setPagedRows((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
		},
	});

	const matches = matchData?.matchPantoneColor ?? [];
	const rows: CatalogueRow[] =
		mode === 'match'
			? matches.map((m) => ({ color: m.color, accuracy: m.accuracy as string | null }))
			: pagedRows;

	const total = listData?.getAllPantoneColor.pageInfo.totalCount ?? 0;
	/* Only the FIRST page blocks the list — later pages land under what is
	   already on screen, so the rows stay put while more arrive. */
	const loading = mode === 'match' ? matching : listing && page === 1;
	const nothingClose =
		mode === 'match' && matches.length > 0 && matches[0].accuracy === 'distant';

	/* Grouped rather than filtered — the book becomes a heading you read past
	   instead of a control you operate. Insertion order is kept, so within a
	   match the closest book still leads. */
	const groups: { title: string; rows: CatalogueRow[] }[] = [];
	for (const row of rows) {
		const group = groups.find((g) => g.title === row.color.bookTitle);
		if (group) group.rows.push(row);
		else groups.push({ title: row.color.bookTitle, rows: [row] });
	}

	const pick = (color: CatalogueRow['color']) => {
		/* Book and code only. The row id is not stable across a re-seed, so it
		   is never persisted or sent back — and `hex` is left off the write so
		   the backend fills in the book's own value, which is right by
		   definition. The hex travels separately, as a suggestion for the
		   screen colour. */
		onChange({ book: color.book, code: color.code }, color);
		setOpen(false);
		setSearch('');
	};

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setSearch('');
			}}
		>
			<PopoverTrigger
				className={cn(
					'flex w-full cursor-pointer items-center justify-between gap-2 outline-none hover:border-line-3',
					fieldSurface,
					fieldSize,
					fieldFocus,
					!value && 'text-muted-foreground'
				)}
			>
				{value ? (
					<span className="flex min-w-0 items-center gap-2">
						<span
							className="h-4 w-4 shrink-0 rounded-[var(--r-1)] border border-line-2"
							style={{
								backgroundColor: value.hex
									? `#${value.hex.replace('#', '')}`
									: 'var(--line-2)',
							}}
						/>
						<span className="truncate font-mono text-text-1">{value.code}</span>
						<span className="truncate text-xs text-text-4">
							{bookLabel(pantoneBookTitle(value.book))}
						</span>
					</span>
				) : (
					<span className="truncate">Not specified</span>
				)}

				<span className="flex shrink-0 items-center gap-1">
					{value && (
						<span
							role="button"
							aria-label="Clear Pantone reference"
							onClick={(e) => {
								e.stopPropagation();
								onChange(null);
							}}
							className="flex h-4 w-4 items-center justify-center rounded-full text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
						>
							<X size={12} />
						</span>
					)}
					<ChevronDown size={14} className="text-muted-foreground" />
				</span>
			</PopoverTrigger>

			<PopoverContent align="start" className="w-(--anchor-width) min-w-[20rem] gap-0 p-0">
				<SelectSearchInput
					value={search}
					onChange={setSearch}
					placeholder="Search a code — 186 C, 19-5918 TCX"
				/>

				<div className="flex items-center gap-3 border-b border-line-1 px-2.5 py-1.5">
					{query ? (
						<span className="text-2xs uppercase tracking-[0.08em] text-text-4">
							{total.toLocaleString()} matching
						</span>
					) : (
						<>
							{canMatch && (
								<ModeTab
									active={!browsing}
									onClick={() => setBrowsing(false)}
									title={`Ranked against ${hex}`}
								>
									Nearest matches
								</ModeTab>
							)}
							<ModeTab
								active={browsing || !canMatch}
								onClick={() => setBrowsing(true)}
							>
								All
							</ModeTab>
						</>
					)}
					<button
						type="button"
						onClick={() => setAllBooks((v) => !v)}
						className="ml-auto shrink-0 text-2xs text-text-4 underline-offset-2 hover:text-text-2 hover:underline"
					>
						{allBooks ? 'Solid only' : 'All Pantone'}
					</button>
				</div>

				{nothingClose && (
					<p className="border-b border-line-1 px-2.5 py-1.5 text-xs text-status-warn">
						No Pantone is close to this colour — it is likely outside what spot ink
						reaches.
					</p>
				)}

				<div className="scroll-thin max-h-64 overflow-y-auto">
					{loading && <p className="px-2.5 py-3 text-xs text-text-4">Searching…</p>}

					{!loading && rows.length === 0 && (
						<p className="px-2.5 py-3 text-xs text-text-4">
							{query
								? `Nothing matches “${query}”.`
								: 'No Pantone book is seeded in this environment.'}
						</p>
					)}

					{!loading &&
						groups.map((group) => (
							<div key={group.title}>
								<div className="sticky top-0 flex items-baseline justify-between gap-2 border-b border-line-1 bg-popover px-2.5 py-1">
									<span className="text-2xs font-medium text-text-4">
										{bookLabel(group.title)}
									</span>
									{mode === 'browse' && (
										/* Says why the list opens on yellows: that is
										   where the printed book opens. Without it the
										   order reads as a fault, not a fan deck. */
										<span
											className="shrink-0 text-2xs text-text-4"
											title="The order the printed book runs: yellow, orange, red, pink, purple, blue, green, then neutrals."
										>
											fan-deck order
										</span>
									)}
								</div>
								{group.rows.map(({ color, accuracy }) => {
									const taken =
										value?.code.toUpperCase() === color.code.toUpperCase() &&
										value?.book === color.book;
									return (
										<button
											key={color.id}
											type="button"
											disabled={taken}
											onClick={() => pick(color)}
											className={cn(
												'flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left transition-colors',
												taken
													? 'cursor-default opacity-45'
													: 'hover:bg-surface-hover'
											)}
										>
											<span
												className="h-5 w-5 shrink-0 rounded-[var(--r-1)] border border-line-2"
												style={{ backgroundColor: `#${color.hex}` }}
											/>
											<span className="whitespace-nowrap font-mono text-sm text-text-1">
												{color.code}
											</span>
											<span className="min-w-0 flex-1 truncate text-xs text-text-4">
												{color.name ?? ''}
											</span>
											{taken ? (
												<span className="shrink-0 text-2xs text-text-4">
													Selected
												</span>
											) : (
												accuracy && <AccuracyBadge accuracy={accuracy} />
											)}
										</button>
									);
								})}
							</div>
						))}

					{/* Never silently truncated, and never a dead end. */}
					{!loading && mode !== 'match' && total > rows.length && (
						<button
							type="button"
							onClick={() => setPage((p) => p + 1)}
							disabled={listing}
							className="flex w-full items-center justify-center border-t border-line-1 px-2.5 py-2 text-2xs text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:text-text-4"
						>
							{listing
								? 'Loading…'
								: `Load more — ${rows.length} of ${total.toLocaleString()}`}
						</button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}

/** One of the two ways of looking. Text rather than a framed control: it sits in
 * a strip under the search box, and a segmented control there would outweigh the
 * field it belongs to. */
function ModeTab({
	active,
	onClick,
	title,
	children,
}: {
	active: boolean;
	onClick: () => void;
	title?: string;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={title}
			className={cn(
				'shrink-0 border-b pb-0.5 text-2xs uppercase tracking-[0.08em] transition-colors',
				active
					? 'border-text-3 text-text-2'
					: 'border-transparent text-text-4 hover:text-text-2'
			)}
		>
			{children}
		</button>
	);
}

function AccuracyBadge({ accuracy }: { accuracy: string }) {
	const band = PANTONE_ACCURACY[accuracy as keyof typeof PANTONE_ACCURACY];
	if (!band) return null;
	return (
		<span
			title={band.hint}
			className={cn(
				'shrink-0 whitespace-nowrap text-2xs font-medium',
				band.tone === 'success' && 'text-status-success',
				band.tone === 'warn' && 'text-status-warn',
				band.tone === 'danger' && 'text-status-danger'
			)}
		>
			{band.label}
		</span>
	);
}
